// src/admin/pages/AdminBlogsPage.tsx
import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Alert,
  TextField,
  Chip,
  IconButton,
  Dialog,
  Divider,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PhotoIcon from "@mui/icons-material/Photo";

import {
  ADMIN_BLOGS_QUERY,
  ADMIN_DELETE_BLOG_MUTATION,
  ADMIN_UPSERT_BLOG_MUTATION,
} from "../../graphql/adminBlogs";

import { uploadBlogImages } from "../../api/blogImages";
import { buildImageUrl } from "../../config";
import RichTextEditor from "../../components/RichTextEditor";

type BlogRow = {
  id: string;
  title: string;
  slug?: string;
  excerpt?: string;
  coverImage?: string;
  status: "draft" | "published";
  publishedAt?: string | null;
  createdAt?: string;
  content: string;
};

type BlogForm = {
  id?: string;
  title: string;
  excerpt: string;
  coverImage: string;
  status: "draft" | "published";
  publishedAtLocal: string;
  content: string;
};

const formatDate = (iso?: string | null) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const stripHtml = (html: string) => html.replace(/<[^>]*>/g, "");

const toDateTimeLocal = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

export default function AdminBlogsPage() {
  const { data, loading, error, refetch } = useQuery(ADMIN_BLOGS_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  const [upsertBlog, upsertState] = useMutation(ADMIN_UPSERT_BLOG_MUTATION);
  const [deleteBlog, deleteState] = useMutation(ADMIN_DELETE_BLOG_MUTATION);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [openForm, setOpenForm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<BlogRow | null>(null);

  const [form, setForm] = useState<BlogForm>({
    title: "",
    excerpt: "",
    coverImage: "",
    status: "draft",
    publishedAtLocal: "",
    content: "",
  });

  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const rows: BlogRow[] = (data?.blogs ?? []).map((b: any) => ({
    id: b.id,
    title: b.title,
    slug: b.slug,
    excerpt: b.excerpt ?? "",
    coverImage: b.coverImage ?? "",
    status: b.status,
    publishedAt: b.publishedAt,
    createdAt: b.createdAt,
    content: b.content ?? "",
  }));

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.title?.toLowerCase().includes(q) ||
        r.slug?.toLowerCase().includes(q) ||
        r.status?.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const paged = useMemo(() => {
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const openCreate = () => {
    setUploadError(null);
    setForm({
      title: "",
      excerpt: "",
      coverImage: "",
      status: "draft",
      publishedAtLocal: "",
      content: "",
    });
    setOpenForm(true);
  };

  const openEdit = (row: BlogRow) => {
    setUploadError(null);
    setForm({
      id: row.id,
      title: row.title ?? "",
      excerpt: row.excerpt ?? "",
      coverImage: row.coverImage ?? "",
      status: row.status ?? "draft",
      publishedAtLocal: toDateTimeLocal(row.publishedAt),
      content: row.content ?? "",
    });
    setOpenForm(true);
  };

  const handleCoverFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadError(null);
    setUploadingCover(true);

    try {
      const uploaded = await uploadBlogImages(files);
      const first = uploaded?.[0] || "";
      if (!first) throw new Error("Upload returned no image path");

      setForm((p) => ({ ...p, coverImage: first }));
      e.target.value = ""; // reset
    } catch (err: any) {
      setUploadError(err?.message || "Failed to upload cover image");
    } finally {
      setUploadingCover(false);
    }
  };

  const handleSave = async () => {
    const res = await upsertBlog({
      variables: {
        id: form.id || null,
        title: form.title.trim(),
        excerpt: form.excerpt.trim() || null,
        content: form.content || "",
        coverImage: form.coverImage?.trim() ? form.coverImage.trim() : null,
        status: form.status,
        publishedAt:
          form.status === "published"
            ? form.publishedAtLocal?.trim()
              ? new Date(form.publishedAtLocal).toISOString()
              : new Date().toISOString()
            : null,
      },
    });

    const errors = res?.data?.adminUpsertBlog?.errors ?? [];
    if (errors.length) return;

    setOpenForm(false);
    await refetch();
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const res = await deleteBlog({ variables: { id: confirmDelete.id } });
    const ok = res?.data?.adminDeleteBlog?.success;
    if (ok) {
      setConfirmDelete(null);
      await refetch();
    }
  };

  const formErrors: string[] = upsertState.data?.adminUpsertBlog?.errors ?? [];

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        mb={2}
        spacing={2}
      >
        <Box>
          <Typography variant="h5">Blogs</Typography>

          <Alert severity="info" sx={{ mt: 2 }}>
            • Cover images should be <strong>1200x800</strong> ideally. <br />•
            Blogs can be accessed at <strong>/blogs</strong>
          </Alert>
        </Box>

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <TextField
            size="small"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
          >
            New Blog
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load blogs: {error.message}
        </Alert>
      )}

      {deleteState.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {deleteState.error.message}
        </Alert>
      )}

      <Paper>
        <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 90 }}>Cover</TableCell>
                <TableCell sx={{ minWidth: 220 }}>Title</TableCell>
                <TableCell sx={{ minWidth: 180 }}>Slug</TableCell>
                <TableCell sx={{ width: 120 }}>Status</TableCell>
                <TableCell sx={{ width: 180 }}>Published</TableCell>
                <TableCell sx={{ width: 180 }}>Created</TableCell>
                <TableCell sx={{ width: 260, maxWidth: 260 }}>
                  Content
                </TableCell>
                <TableCell align="right" sx={{ width: 120 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paged.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No blogs found.
                  </TableCell>
                </TableRow>
              )}

              {paged.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    {r.coverImage ? (
                      <Box
                        component="img"
                        loading="lazy"
                        decoding="async"
                        src={buildImageUrl(r.coverImage)}
                        alt="cover"
                        sx={{
                          width: 56,
                          height: 42,
                          objectFit: "cover",
                          borderRadius: 1,
                          border: "1px solid rgba(0,0,0,0.08)",
                        }}
                      />
                    ) : (
                      <Box sx={{ color: "text.disabled", fontSize: 12 }}>—</Box>
                    )}
                  </TableCell>

                  <TableCell>{r.title}</TableCell>
                  <TableCell>{r.slug}</TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      label={r.status}
                      variant="outlined"
                      sx={{ textTransform: "capitalize", borderRadius: 1 }}
                    />
                  </TableCell>

                  <TableCell>{formatDate(r.publishedAt)}</TableCell>
                  <TableCell>{formatDate(r.createdAt)}</TableCell>

                  <TableCell sx={{ width: 260, maxWidth: 260 }}>
                    <Box
                      sx={{
                        width: 260,
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        color: "text.secondary",
                        fontSize: 13,
                        display: "block",
                      }}
                      title={stripHtml(r.content || "")}
                    >
                      {stripHtml(r.content || "")}
                    </Box>
                  </TableCell>

                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(r)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setConfirmDelete(r)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* FORM DIALOG */}
      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        maxWidth="md"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {form.id ? "Edit Blog" : "Create Blog"}
          </Typography>

          <Divider sx={{ my: 2 }} />

          {(formErrors.length > 0 || uploadError) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {[...formErrors, ...(uploadError ? [uploadError] : [])].join(
                " • ",
              )}
            </Alert>
          )}

          <Stack spacing={2}>
            <TextField
              label="Title"
              value={form.title}
              onChange={(e) =>
                setForm((p) => ({ ...p, title: e.target.value }))
              }
              fullWidth
            />

            <TextField
              label="Excerpt (meta description)"
              value={form.excerpt}
              onChange={(e) =>
                setForm((p) => ({ ...p, excerpt: e.target.value }))
              }
              fullWidth
              multiline
              minRows={2}
            />

            {/* COVER IMAGE UPLOAD (same pattern as campaigns) */}
            <Stack spacing={1}>
              <Typography variant="subtitle2">Cover Image</Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<PhotoIcon />}
                  disabled={uploadingCover}
                >
                  {uploadingCover ? "Uploading..." : "Select from Computer"}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleCoverFileChange}
                  />
                </Button>

                <TextField
                  label="Cover Image URL / Path"
                  value={form.coverImage}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, coverImage: e.target.value }))
                  }
                  fullWidth
                  placeholder="/uploads/blogs/... or https://..."
                />
              </Stack>

              {/* Preview */}
              {form.coverImage?.trim() ? (
                <Box
                  sx={{
                    mt: 1,
                    border: "1px solid rgba(0,0,0,0.10)",
                    width: "100%",
                    maxWidth: 520,
                  }}
                >
                  <Box
                    component="img"
                    loading="lazy"
                    decoding="async"
                    src={buildImageUrl(form.coverImage)}
                    alt="cover preview"
                    sx={{
                      width: "100%",
                      height: 220,
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No cover image selected.
                </Typography>
              )}
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                select
                label="Status"
                value={form.status}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    status: e.target.value as "draft" | "published",
                    publishedAtLocal:
                      e.target.value === "draft" ? "" : p.publishedAtLocal,
                  }))
                }
                fullWidth
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="published">Published</MenuItem>
              </TextField>

              <TextField
                label="Published At"
                type="datetime-local"
                value={form.publishedAtLocal}
                onChange={(e) =>
                  setForm((p) => ({ ...p, publishedAtLocal: e.target.value }))
                }
                fullWidth
                disabled={form.status !== "published"}
                InputLabelProps={{ shrink: true }}
                helperText={
                  form.status !== "published"
                    ? "Enable publishing to set date/time"
                    : "Select date + time (hour/minutes)"
                }
              />
            </Stack>

            <Box>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, color: "text.secondary" }}
              >
                Content
              </Typography>
              <RichTextEditor
                value={form.content}
                onChange={(html) => setForm((p) => ({ ...p, content: html }))}
                placeholder="Write your blog content here..."
              />
            </Box>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              onClick={() => setOpenForm(false)}
              disabled={upsertState.loading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={
                upsertState.loading ||
                uploadingCover ||
                form.title.trim().length === 0
              }
            >
              {upsertState.loading ? "Saving..." : "Save"}
            </Button>
          </Stack>
        </Box>
      </Dialog>

      {/* DELETE CONFIRM */}
      <Dialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Delete Blog?
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            This will permanently delete: <b>{confirmDelete?.title}</b>
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              onClick={() => setConfirmDelete(null)}
              disabled={deleteState.loading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={deleteState.loading}
            >
              {deleteState.loading ? "Deleting..." : "Delete"}
            </Button>
          </Stack>
        </Box>
      </Dialog>
    </Box>
  );
}
